import { Controller } from '../utils/controller';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ymkAsyncInit: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    YMK: any;
  }
}

interface IFoundationShade {
  hex?: string;
  cooler?: string;
  lighter?: string;
  guid?: string;
  darker?: string;
  warmer?: string;
}

// Event types
const CHOOSE_MODEL_ICON_CLICKED_EVENT = 'chooseModelIconClicked';
const LAUNCHER_PAGE_ENTERED_EVENT = 'launcherPageEntered';
const SPLIT_FOUR_WAY_EVENT = 'splitFourWay';
const CLOSED_EVENT = 'closed';
const CAMERA_OPENED = 'cameraOpened';
const CAMERA_CLOSED = 'cameraClosed';
const ENGINE_CLOSED_EVENT = 'engineClosed';
const LIVE_CAM_ICON_CLICKED_EVENT = 'liveCamIconClicked';
const LOAD_IMAGE_BY_MODEL_EVENT = 'loadImageByModel';
const LOADED_EVENT = 'loaded';
const LOADING_EVENT = 'loading';
const OPENED_EVENT = 'opened';
const SKIN_ANALYSIS_FINISHED_EVENT = 'skinAnalysisFinished';
const SKIN_SHADE_FOUND_EVENT = 'skinShadeFound';
const UPLOAD_PHOTO_ICON_CLICKED_EVENT = 'uploadPhotoIconClicked';
const VTO_ACTIONS_TRACKING_EVENT = 'VTOActionsTracking';

const ATB_BUTTON_ID = 'vto-add-to-bag-button';
const CAMERA = 'camera';
const CANVAS_ID = 'result-canvas';
const CTA_ACTION = 'cta';
const DESKTOP_BREAKPOINT = 1024;
const FOUNDATION = 'foundation';
const MODULE_IFRAME_ID = 'YMK-module-iframe';
const MODULE_VIDEO_INPUT_ID = 'YMK-video-input';
const MAKEUP = 'makeup';
const MODEL = 'model';
const MODULE_ID = 'YMK-module';
const RESULT_ID = 'result-loader';
const SKINCARE = 'skincare';
const UPLOAD = 'upload';

const foundationShades: IFoundationShade[] = [
  { hex: '#e5ad8a' },
  { hex: '#f2bf91' },
  { hex: '#c48f56' },
  { hex: '#c27440' },
  { hex: '#d69060' }
];

export default class extends Controller {
  static targets = [
    'productCarousel',
    'vtoContainer',
    'launchButton',
    'vtoMobileWrapper',
    'vtoShadeCarousel',
    'variantShade',
    'shadesContainer',
    'shadesDropdownText',
    'shadesDropdownSelector',
    'shadesTab',
    'trigger',
    'pagination',
    'shadeDescription'
  ];
  static requiredTargets = [];

  static values = {
    autoOpen: Boolean,
    foundationShades: {
      type: Object,
      default: {}
    },
    locale: {
      type: String,
      default: 'en_US'
    },
    isMobile: Boolean,
    isYMKModuleLoaded: Boolean,
    isYMKModuleOpen: Boolean,
    shadeLocalizedPath: {
      type: String,
      default: ''
    },
    shades: {
      type: Array,
      default: []
    },
    activeVariant: String,
    apikey: String,
    id: String,
    type: String,
    skincareResults: Array,
    productIdList: {
      type: Array,
      default: ['22830']
    },
    width: Number,
    height: Number,
    enabled: {
      type: Boolean,
      default: false
    },
    brand: String
  };

  // TARGETS
  declare paginationTarget: HTMLDivElement;
  declare shadeDescriptionTarget: HTMLSpanElement;
  declare launchButtonTarget: HTMLButtonElement;
  declare productCarouselTarget: HTMLDivElement;
  declare shadesDropdownTextTarget: HTMLSpanElement;
  declare vtoContainerTarget: HTMLDivElement;
  declare variantShadeTargets: HTMLAnchorElement[];
  declare shadesContainerTarget: HTMLDivElement;
  declare shadesDropdownSelectorTargets: HTMLAnchorElement[];
  declare shadesTabTargets: HTMLButtonElement[];
  declare triggerTarget: HTMLButtonElement;
  declare hasLaunchButtonTarget: boolean;
  declare hasProductCarouselTarget: boolean;
  declare hasShadesDropdownTextTarget: boolean;
  declare hasVtoContainerTarget: boolean;
  declare hasVariantShadeTargets: boolean;
  declare hasShadesContainerTarget: boolean;
  declare hasShadesDropdownSelectorTargets: boolean;
  declare hasTriggerTarget: boolean;
  declare hasPaginationTarget: boolean;
  declare hasShadeDescriptionTarget: boolean;

  // VALUES
  private activeVariantValue: string | undefined;
  private apikeyValue: string;
  private foundationShadesValue: IFoundationShade;
  private idValue: string;
  private isYMKModuleLoadedValue: boolean;
  private enabledValue: boolean;
  private localeValue: string;
  private mediaValue: string;
  private payloadEventValueCameraEnd: number;
  private payloadEventValueCameraStart: number;
  private productIdListValue: string[];
  private shadeLocalizedPathValue: string;
  private typeValue: string;
  private brandValue: string;

  private shadesObserver: MutationObserver | null = null;

  initialize() {
    if (this.enabledValue) {
      this.loadYMK();
    }
  }

  isMobile() {
    return screen.width < DESKTOP_BREAKPOINT;;
  }

  isDesktop() {
    return screen.width >= DESKTOP_BREAKPOINT;
  }

  launchVTO() {
    if (this.hasVtoContainerTarget) {
      this.vtoContainerTarget.style.display = '';
    }

    if (this.hasLaunchButtonTarget) {
      this.launchButtonTarget.disabled = true;
    }

    if (this.hasProductCarouselTarget) {
      this.productCarouselTarget.style.visibility = 'hidden';
    }

    if (this.hasPaginationTarget) {
      this.paginationTarget.style.visibility = 'hidden';
    }

    // hide swiper elements when VTO is active
    const swiperElements = window.document.querySelectorAll(
      '.mobile-image-zoom-button,' +
      ' .product-details-badge-text,' +
      ' .swiper-button-next,' +
      ' .swiper.pincer-swiper.swiper-container,' +
      ' .swiper-button-prev'
    );

    swiperElements.forEach(e => e.style.visibility = 'hidden');

    const width = this.isMakeup() ? this.vtoContainerTarget.offsetWidth : screen.width
    const height = this.isMakeup() ? this.vtoContainerTarget.offsetHeight : screen.height;

    this.createYMKElement();

    if (this.isYMKModuleLoadedValue) {
      this.openVTOByType(this.typeValue);
    }

    window.ymkAsyncInit = () => {
      window.YMK.init({
        autoOpen: !this.isSkincare(),
        showCloseBtnOnLauncher: true,
        language: this.languageMapper(this.localeValue),
        height,
        width,
        ...(this.isFoundation() && { productIdList: this.productIdListValue })
      });

      if (this.isFoundation() || this.isSkincare()) {
        this.openVTOByType(this.typeValue);
      }

      window.dispatchEvent(
        new CustomEvent(VTO_ACTIONS_TRACKING_EVENT, {
          detail: {
            event_action: CTA_ACTION,
            vto_type: this.typeValue,
            vto_action: CTA_ACTION
          }
        })
      );

      this.addYMKEventListeners();
    };

    window.ymkAsyncInit();
  }

  loadYMK() {
    if (!this.idValue || !this.apikeyValue) {
      return;
    }

    const apiUrl = `https://plugins-media.makeupar.com/${this.idValue}/sdk.js?apiKey=${this.apikeyValue}`;
    const scriptLoaded = Array.from(document.getElementsByTagName('script')).find(
      (script) => script.src === apiUrl
    );

    if (scriptLoaded) {
      return;
    }

    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = apiUrl;
    document.body.appendChild(s);
  }

  getURLSubdirectoryByType(type) {
    switch (type) {
      case MAKEUP:
        return '';
      case FOUNDATION:
        return '-shadefinder';
      case SKINCARE:
        return '-skincare';
      default:
        return '';
    }
  }

  openVTOByType(type) {
    switch (type) {
      case MAKEUP:
        return window.YMK.open();
      case FOUNDATION:
        return window.YMK.open(true, null, true);
      case SKINCARE:
        return window.YMK.openSkincare('capture');
      default:
        return window.YMK.open();
    }
  }

  createYMKElement() {
    const ymkElement = document.getElementById(MODULE_ID);
    if (ymkElement != null) {
      return;
    }

    const YMKElement = document.createElement('div');
    const height = this.vtoContainerTarget.offsetHeight;

    YMKElement.id = MODULE_ID;
    YMKElement.style.height = height + 'px';

    this.vtoContainerTarget.appendChild(YMKElement);
  }

  addYMKEventListeners() {
    const vtoEvents = [
      CHOOSE_MODEL_ICON_CLICKED_EVENT,
      LIVE_CAM_ICON_CLICKED_EVENT,
      LOAD_IMAGE_BY_MODEL_EVENT,
      UPLOAD_PHOTO_ICON_CLICKED_EVENT,
      'cameraClosed',
      'cameraFailed',
      'cameraOpened',
      'compare',
      'compareDisabled',
      'splitFourWay',
      'splitFourWayDisabled',
      'photoLoaded'
    ];

    vtoEvents.forEach(
      function (eventAction) {
        window.YMK.addEventListener(
          eventAction,
          function () {
            if (eventAction === LIVE_CAM_ICON_CLICKED_EVENT) {
              this.mediaValue = CAMERA;
            } else if (eventAction === CHOOSE_MODEL_ICON_CLICKED_EVENT) {
              this.mediaValue = MODEL;
            } else if (eventAction === UPLOAD_PHOTO_ICON_CLICKED_EVENT) {
              this.mediaValue = UPLOAD;
            }

            window.dispatchEvent(
              new CustomEvent(VTO_ACTIONS_TRACKING_EVENT, {
                detail: {
                  event_action: eventAction,
                  vto_type: this.typeValue,
                  vto_media_type: this.mediaValue
                }
              })
            );
          }.bind(this)
        );
      }.bind(this)
    );

    window.YMK.addEventListener(LOADING_EVENT, (progress) => {
      progress === 10 && this.resizeVTO(LOADING_EVENT);
    });

    window.YMK.addEventListener(LOADED_EVENT, () => {
      this.isYMKModuleLoadedValue = true;
      this.payloadEventValueCameraEnd = performance.now();
      const eventValue = Math.floor(
        this.payloadEventValueCameraEnd - this.payloadEventValueCameraStart
      );
      this.resizeVTO(LOADED_EVENT, eventValue);

      if (this.isMakeup()) {
        window.YMK.applyMakeupBySku(this.activeVariantValue);
      }
    });

    if (this.isMakeup()) {
      this.attachVTOEventToShades();

      // Shade tabs re-render the shade list, so we use a MutationObserver
      // to reassign event listeners to new elements as they are added
      this.shadesObserver = new MutationObserver(() => {
        this.attachVTOEventToShades();
      });
      this.shadesObserver.observe(this.shadesContainerTarget, { childList: true, subtree: true });
    }

    [
      OPENED_EVENT, 
      CAMERA_OPENED, 
      CAMERA_CLOSED, 
      ENGINE_CLOSED_EVENT, 
      LAUNCHER_PAGE_ENTERED_EVENT, 
      SPLIT_FOUR_WAY_EVENT, 
      CHOOSE_MODEL_ICON_CLICKED_EVENT, 
      LOAD_IMAGE_BY_MODEL_EVENT
    ].forEach(event => {
      window.YMK.addEventListener(event, () => { this.resizeVTO(event) });  
    });

    window.YMK.addEventListener(SKIN_SHADE_FOUND_EVENT, (result) => {
      this.foundationShadesValue = result.readableRecommendations[0];
      window.YMK.applyMakeupBySku(result.readableRecommendations[0].guid);
      this.generateFoundationShadeOverlay();
    });

    window.YMK.addEventListener(SKIN_ANALYSIS_FINISHED_EVENT, (/*result*/) => {
      document.body.style.overflow = '';
    });

    window.YMK.addEventListener(CLOSED_EVENT, () => {
      this.vtoContainerTarget.style.display = '';
      this.launchButtonTarget.disabled = false;
      this.payloadEventValueCameraEnd = performance.now();

      window.dispatchEvent(
        new CustomEvent(VTO_ACTIONS_TRACKING_EVENT, {
          detail: {
            event_action: CLOSED_EVENT,
            vto_type: this.typeValue,
            vto_media_type: this.mediaValue,
            event_value: Math.floor(
              this.payloadEventValueCameraEnd - this.payloadEventValueCameraStart
            )
          }
        })
      );

      if (this.shadesObserver) {
        this.shadesObserver.disconnect();
        this.shadesObserver = null;
      }

      if (this.shadeLocalizedPathValue) {
        window.location.replace(this.shadeLocalizedPathValue);
        // if location.replace fails force the page to reload after two seconds
        setTimeout(() => {
          location.reload();
        }, 2000);
      } else {
        this.resetYMK();
      }
    });
  }

  attachVTOEventToShades() {
    for (const [index, element] of this.variantShadeTargets.entries()) {
      const vtoEnabled = element.dataset['vtoShadeEnabled'];
      delete element.dataset['phxLink'];
      delete element.dataset['phxLinkState'];

      delete this.shadesDropdownSelectorTargets[index].dataset['phxLink'];
      delete this.shadesDropdownSelectorTargets[index].dataset['phxLinkState'];
      this.shadesDropdownSelectorTargets[index].dataset.action =
        'shades-dropdown#toggleListDisplay';

      const localizedPath = element.href;
      const label = element.querySelector('label');
      element.href = '#';
      this.shadesDropdownSelectorTargets[index].href = '#';

      if (vtoEnabled) {
        const selectedSku = element.dataset['vtoSelectedSku'];
        const vtoSku = element.querySelector('input')?.value;
        const shadeDiv = element.querySelector('[data-shade]') as HTMLDivElement;
        const skuShade = shadeDiv?.dataset['shade'];
        const description = element.dataset['shadeDescription'];

        if (vtoSku === this.activeVariantValue) {
          this.shadeLocalizedPathValue = localizedPath;
        }

        if (label) {
          label.style.cursor = 'pointer';
        }

        element.dataset.action = 'product#updateSelectedSkuViaEvent';
        element.dataset.skuid = selectedSku;

        const changeVTOShade = () => {
          this.activeVariantValue = vtoSku;
          this.shadeLocalizedPathValue = localizedPath;
          this.shadesDropdownTextTarget.innerText = label?.title || '';
          this.setDropdownColor(skuShade);
          this.setDescription(description);
          this.unselectAll();
          this.setSelected(element, index);
          window.YMK.applyMakeupBySku(vtoSku);
          this.updateShadeParam(label?.title);
        };

        element.onclick = changeVTOShade;
        this.shadesDropdownSelectorTargets[index].onclick = changeVTOShade;
      } else {
        element.style.opacity = '0.6';

        if (label) {
          label.style.cursor = 'not-allowed';
        }
      }
    }
  }

  updateShadeParam(shade) {
    if (!shade) {
      return;
    }

    const url = new URL(window.location.href);
    const shadeParam = shade.replace(/\s+/g, "_") || ""
    url.searchParams.set('shade', shadeParam);
    window.history.replaceState({}, '', url.toString());

  }

  setDescription(description) {
    if (this.hasShadeDescriptionTarget && description) {
      this.shadeDescriptionTarget.textContent = description;
    }
  }

  setDropdownColor(color) {
    const shadesDropdown = this.triggerTarget.querySelector('[data-shade]') as HTMLDivElement;

    if (shadesDropdown && color) {
      shadesDropdown.style.backgroundColor = color;
    }
  }

  unselectAll() {
    this.variantShadeTargets.forEach((el, i) => this.removeSelected(el, i));
  }

  removeSelected(shade, index) {
    const selectedVariant = shade.querySelector('.shade-selected');

    if (selectedVariant) {
      selectedVariant.classList.remove('shade-selected');
    }

    const shadEl = this.shadesDropdownSelectorTargets[index].parentNode as HTMLElement;
    shadEl.classList.remove('checked');
  }

  setSelected(shade, index) {
    const selectedVariant = shade.querySelector('.shade-swatch');

    if (selectedVariant) {
      selectedVariant.classList.add('shade-selected');
    }

    const shadSelectorEl = this.shadesDropdownSelectorTargets[index].parentNode as HTMLElement;
    shadSelectorEl.classList.add('checked');
  }

  resetYMK() {
    const ymkElement = document.getElementById(MODULE_ID);
    this.vtoContainerTarget.style.display = 'none';
    this.productCarouselTarget.style.visibility = 'visible';
    this.paginationTarget.style.visibility = 'visible';
    ymkElement?.remove();
    window.YMK.reset();
  }

  generateFoundationShadeButton(hex, sku) {
    const button = document.createElement('button');
    button.style.background = hex;
    button.onclick = () => window.YMK.applyMakeupBySku(sku);

    return button;
  }

  generateFoundationShadeOverlay() {
    const shadeContainer = document.createElement('div');

    const cooler = this.generateFoundationShadeButton(
      foundationShades[0].hex,
      this.foundationShadesValue.cooler
    );
    const lighter = this.generateFoundationShadeButton(
      foundationShades[1].hex,
      this.foundationShadesValue.lighter
    );
    const best = this.generateFoundationShadeButton(
      foundationShades[2].hex,
      this.foundationShadesValue.guid
    );
    const darker = this.generateFoundationShadeButton(
      foundationShades[3].hex,
      this.foundationShadesValue.darker
    );
    const warmer = this.generateFoundationShadeButton(
      foundationShades[4].hex,
      this.foundationShadesValue.warmer
    );

    shadeContainer.appendChild(cooler);
    shadeContainer.appendChild(lighter);
    shadeContainer.appendChild(best);
    shadeContainer.appendChild(darker);
    shadeContainer.appendChild(warmer);

    const YMKElement = document.getElementById(MODULE_ID);
    const YMKiFrame = document.getElementById(MODULE_IFRAME_ID);

    const wrapper = document.createElement('div');

    wrapper.appendChild(shadeContainer);

    const addToBagTemplate = document.getElementById(ATB_BUTTON_ID);
    const addToBagTemplateContent = (addToBagTemplate as HTMLTemplateElement).content;
    const clonedContent = addToBagTemplateContent.cloneNode(true);

    wrapper.append(shadeContainer);
    wrapper.appendChild(clonedContent);

    if (YMKiFrame) {
      YMKiFrame.style.height = `${screen.height - 208}px`;
    }
    YMKElement?.appendChild(wrapper);
  }

  resizeVTO(event_action: string, event_value = 0) {
    const YMKElement = document.getElementById(MODULE_ID);
    const DELAY = 500;
    if (!YMKElement) {
      return;
    }
    this.payloadEventValueCameraStart = performance.now();

    window.dispatchEvent(
      new CustomEvent(VTO_ACTIONS_TRACKING_EVENT, {
        detail: {
          event_action,
          vto_type: this.typeValue,
          vto_media_type: this.mediaValue,
          event_value
        }
      })
    );

    YMKElement.style.display = 'none';

    setTimeout(() => {
      const YMKiFrame = document.getElementById(MODULE_IFRAME_ID) as HTMLIFrameElement;
      const YMKVideoInput = document.getElementById(MODULE_VIDEO_INPUT_ID) as HTMLIFrameElement;

      if (YMKiFrame) {
        const height = this.vtoContainerTarget.offsetHeight;
        const width = this.vtoContainerTarget.offsetWidth;

        const YMKElementFirstDiv = YMKElement.querySelector('div');
        const iframeDocument = YMKiFrame?.contentWindow?.document;
        const frameContent = iframeDocument?.querySelector('.frame-content');
        const frameFirstDiv = frameContent && frameContent.querySelector('div');
        const resultCanvas = iframeDocument?.getElementById(CANVAS_ID);

        const eventHandlers = {
          [CHOOSE_MODEL_ICON_CLICKED_EVENT]: this.resizeModelImages,
          [ENGINE_CLOSED_EVENT]: this.resizeEngineClosed,
          [LAUNCHER_PAGE_ENTERED_EVENT]: this.resizeLauncherPage,
          [SPLIT_FOUR_WAY_EVENT]: this.resizeSplitFourWay,
          [CAMERA_OPENED]: this.resizeCameraScreen,
          [CAMERA_CLOSED]: this.resizeCameraClosedScreen,
          [LOAD_IMAGE_BY_MODEL_EVENT]: this.resizeLoadImageByModel,
        };

        if (event_action in eventHandlers) {
          eventHandlers[event_action].call(this, frameContent);
        }

        if (YMKElementFirstDiv) {
          YMKElementFirstDiv.style.height = height + 'px';
          YMKElementFirstDiv.style.width = width + 'px';
        }

        if (frameFirstDiv) {
          frameFirstDiv.style.height = height + 'px';
          frameFirstDiv.style.width = width + 'px';
        }

        YMKiFrame.style.height = height + 'px';
        YMKiFrame.style.width = width + 'px';

        if (YMKVideoInput) {
          YMKVideoInput.style.height = height + 'px';
          YMKVideoInput.style.width = width + 'px';
        }

        if (resultCanvas) {
          resultCanvas.style.height = height + 'px';
          resultCanvas.style.width = width + 'px';
        }
      }

      YMKElement.style.display = '';
    }, DELAY);
  }

  languageMapper(locale) {
    const languageMap = {
      cs_: 'cze',
      de_: 'deu',
      da_: 'dnk',
      en_: 'enu',
      es_: 'esp',
      fi_: 'fin',
      fr_: 'fra',
      it_: 'ita',
      ja_: 'jpn',
      ko_: 'kor',
      nl_: 'dut',
      no_: 'nor',
      pl_: 'pol',
      pt_: 'ptb',
      zh_: 'cht',
      zhhans: 'chs',
      zhhant: 'cht'
    };

    for (const key in languageMap) {
      if (locale.includes(key)) {
        return languageMap[key];
      }
    }

    return 'enu';
  }

  private isMakeup() {
    return this.typeValue === MAKEUP;
  }

  private isSkincare() {
    return this.typeValue === SKINCARE;
  }

  private isFoundation() {
    return this.typeValue === FOUNDATION;
  }

  private resizeCameraScreen(frameContent) {
    // intentially left blank, use event when needed.
  }

  private resizeCameraClosedScreen(frameContent) {
    if (this.brandValue == "Estée Lauder") {
      const container = frameContent.querySelector("body > div > div > div > div > div:nth-child(6) > div:nth-child(1) > div");
      if (container) {
        container.style.marginTop = '15px';
      }
    }
  }

  private resizeSplitFourWay(frameContent) {
    if (this.brandValue == "Clinique") {
      const height = this.vtoContainerTarget.offsetHeight;
      const buttonsContainer = frameContent.querySelector(".frame-content > div > div > div:nth-child(1)");
      buttonsContainer.style.height = `${height}px`;
    }
  }

  private resizeLoadImageByModel(frameContent) {
    if (this.brandValue == "Estée Lauder") {
      const container = frameContent.querySelector(".frame-content > div > div > div:nth-child(3)")
      const imgs = container.querySelectorAll('img')
      imgs.forEach(i => {
        if (/\/webconsultation\/model_images/.test(i.src)) {
          i.style.display = "none"
        }
      })
    }
  }

  private resizeLauncherPage(frameContent) {
    if (this.brandValue == "MAC" && (this.isMobile() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches)) {
      const contentContainer = frameContent.querySelector(".frame-content > div:nth-child(2) > div > div:nth-child(2)");
      contentContainer.style.top = '7%';

      const title = contentContainer.firstElementChild;
      title.style.padding = '0 0 10px 0';
      title.style.fontSize = '20px';
      title.style.lineHeight = '1rem';
      title.style.maxWidth = '100%';
      title.style.textAlign = 'center';

      const orButton = frameContent.querySelector(".frame-content > div:nth-child(2) > div > div:nth-child(2) > div:nth-child(3) > div:nth-child(3)")
      orButton.style.padding = '13px 10px';

      if (contentContainer) {
        const lastChild = contentContainer.lastElementChild;
        if (lastChild) {
          lastChild.style.padding = '0'
        }
      }
    }

    if (this.brandValue == "Clinique") {
      const title = frameContent.querySelector(".frame-content > div > div > div:nth-child(2) > div:nth-child(1)")
      title.style.fontSize = this.isMobile() ? '25px' : '30px';
    }

    if (this.brandValue == "Estée Lauder") {
      const container = frameContent.querySelector(".frame-content > div > div > div:nth-child(2)")
      container.style.transform = 'translateY(-50%)';
      if (this.isMobile()) {
        container.style.padding = '2rem';
      }
    }
  }

  private resizeEngineClosed(frameContent) {
    // engineClosed is fired when going back from Live Camera or a model image to the model picker. 
    // Handle carefully since it runs on multiple screens and a fix for one may break another.
    const gridContainerCL = frameContent.querySelector(".frame-content > div > div > div:nth-child(2) > div > div:nth-child(2)");
    const gridContainerEL = frameContent.querySelector(".frame-content > div > div > div:nth-child(3) > div:nth-child(2)");
    if (gridContainerCL || gridContainerEL) {
      this.resizeModelImages(frameContent);
    }
  }

  private resizeModelImages(frameContent) {
    if (this.brandValue == "Clinique") {
      const generalContainer = frameContent.querySelector(".frame-content > div > div > div:nth-child(2)");
      generalContainer.style.top = '13%';

      const gridContainer = frameContent.querySelector(".frame-content > div > div > div:nth-child(2) > div > div:nth-child(2)");
      gridContainer.style.transform = this.isMobile() ? 'scale(0.6)' : 'scale(0.7)';
      gridContainer.style.transformOrigin = "top center";
      gridContainer.style.maxHeight = "unset";
    }

    if (this.brandValue == "Estée Lauder") {
      if (!this.isDesktop() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        const container = frameContent.querySelector("body > div > div > div > div > div:nth-child(3)")
        container.style.top = '10%';
        container.style.transform = 'none';
        
        const imagesContainer = container?.querySelector("div:nth-child(2)");
        imagesContainer.style.display = "grid";
        imagesContainer.style.gridTemplateColumns = "repeat(3, 1fr)";
        imagesContainer.style.transform = 'scale(0.7)';
        imagesContainer.style.transformOrigin = 'top';

        const imgs = frameContent?.querySelectorAll('img');
        for (const img of imgs) {
          const imgContainer = img.parentElement.parentElement;
          imgContainer.style.width = 'auto';
          imgContainer.style.height = 'auto';
          imgContainer.style.float = 'none';
        }
      }
    }
  }
}
